/**
 * DataSources — live provenance page at `/methodology/data-sources`.
 * Reads /api/methodology/data-sources (5-minute cached backend
 * aggregation) and renders every primary source powering WUA
 * degree records with a live record count.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Database } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DataSources() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/methodology/data-sources`)
      .then((r) => r.json())
      .then(setData)
      .catch((e) => setErr(String(e)));
  }, []);

  return (
    <div className="max-w-5xl mx-auto py-10 px-6" data-testid="data-sources-page">
      <div className="label-uppercase text-[10px] text-stone-500 mb-1">
        Methodology · Data honesty
      </div>
      <h1 className="text-4xl font-editorial mb-4">Where our numbers come from</h1>
      <p className="text-stone-700 leading-relaxed max-w-3xl mb-6">
        Every graduate-outcome figure on WhichUniversityAdvisor.com cites a
        primary source. The table below shows exactly which national statistics
        body, government dataset or university-published report powers each
        record — and how many degrees each source currently serves. If a source
        stops publishing or drifts stale, this page will drop the count. If we
        can't cite a primary source, we don't publish the number.
      </p>

      {err && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded text-sm">
          Couldn't load provenance data: {err}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <StatTile
              label="Total degrees"
              value={data.total_degrees.toLocaleString()}
              testid="ds-total"
            />
            <StatTile
              label="Backed by a primary source"
              value={`${data.total_with_primary_source.toLocaleString()} · ${Math.round(
                (100 * data.total_with_primary_source) / (data.total_degrees || 1)
              )}%`}
              tone="green"
              testid="ds-cited"
            />
            <StatTile
              label="Awaiting real source"
              value={data.unattributed.toLocaleString()}
              tone="amber"
              testid="ds-uncited"
            />
          </div>

          <div className="border-t border-stone-200 pt-6">
            <h2 className="text-lg font-semibold mb-4">
              Primary sources · live counts
            </h2>

            <div className="space-y-3">
              {data.sources.map((s) => (
                <SourceRow key={s.label} s={s} />
              ))}
            </div>

            {data.unmatched_variants?.length > 0 && (
              <details className="mt-6 text-xs text-stone-500">
                <summary className="cursor-pointer">
                  Unattributed evidence strings ({data.unmatched_variants.length})
                </summary>
                <ul className="mt-2 space-y-1 pl-4">
                  {data.unmatched_variants.map(([src, n]) => (
                    <li key={src}>
                      <span className="font-mono text-[11px]">{n}</span> · {src}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <div className="mt-6 text-xs text-stone-500">
              {data.cached
                ? `Live figures · cache age ${data.cache_age_seconds}s (refreshes every 5 min)`
                : "Live figures · just refreshed"}
            </div>
          </div>

          <div className="mt-10 p-5 rounded bg-stone-50 border border-stone-200">
            <div className="label-uppercase text-[10px] text-stone-500 mb-2">
              Why we built this page
            </div>
            <p className="text-sm text-stone-700 leading-relaxed">
              Most comparison sites publish specific graduate salaries and
              employer lists without telling you where the numbers came from.
              We've spent February 2026 auditing our own database, stripping
              fabricated per-programme estimates, and rebuilding every outcome
              on top of an actual government or university-published dataset.
              This page is the receipt.
            </p>
            <Link
              to="/methodology"
              className="inline-flex items-center gap-1 text-sm mt-3 text-[#4C1D95] underline"
              data-testid="ds-methodology-link"
            >
              Read the full methodology <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

function StatTile({ label, value, tone = "default", testid }) {
  const toneMap = {
    default: "bg-white",
    green: "bg-emerald-50 border-emerald-200",
    amber: "bg-amber-50 border-amber-200",
  };
  return (
    <div
      className={`p-4 border rounded ${toneMap[tone]}`}
      data-testid={testid}
    >
      <div className="label-uppercase text-[10px] text-stone-500 mb-1">
        {label}
      </div>
      <div className="text-2xl font-serif">{value}</div>
    </div>
  );
}

function SourceRow({ s }) {
  return (
    <div
      className="flex items-start justify-between gap-4 p-3 border border-stone-200 rounded hover:bg-stone-50 transition-colors"
      data-testid={`ds-src-${s.label.replace(/[^a-zA-Z0-9]/g, "-")}`}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="pt-1 text-stone-400">
          <Database className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="font-semibold text-stone-900">{s.label}</h3>
            {s.country && (
              <span className="text-[10px] px-1.5 py-0.5 bg-stone-200 text-stone-700 rounded uppercase tracking-wider">
                {s.country}
              </span>
            )}
            {s.type && (
              <span className="text-[10px] text-stone-500">· {s.type}</span>
            )}
          </div>
          <div className="text-sm text-stone-600 mt-1">{s.publisher}</div>
          <div className="text-xs text-stone-500 mt-1">{s.metric}</div>
          {s.citation_url && (
            <a
              href={s.citation_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs mt-1.5 text-[#4C1D95] hover:underline"
            >
              View source <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-2xl font-serif">
          {s.records.toLocaleString()}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-stone-500">
          {s.records === 1 ? "record" : "records"}
        </div>
        {s.evidence_variants > 1 && (
          <div className="text-[10px] text-stone-400 mt-1">
            {s.evidence_variants} evidence variants
          </div>
        )}
      </div>
    </div>
  );
}
